/**
 * Stable client-side UID for React `key` props on editor lists where the
 * underlying records have no server-assigned id (services, documents,
 * invoice line items, etc.). Stripped before sending to the backend by
 * the existing per-form `sanitizePayload` (which explicitly picks the
 * fields it serializes).
 *
 * Why a UID at all? Using the array index as a key tears down component
 * state on every reorder/insert/delete — losing focus, scroll, IME
 * composition, and undo history in inline editors.
 */
let _counter = 0;

export function clientUid(prefix = "row") {
  // `crypto.randomUUID` is available in every modern browser this app
  // targets, but feature-detect anyway so SSR / older test envs work.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  _counter += 1;
  return `${prefix}-${Date.now()}-${_counter}`;
}

/**
 * Inject `_uid` into every row of an array that doesn't already have one.
 * Idempotent — rows that already carry a `_uid` are returned untouched.
 */
export function withClientUids(rows, prefix = "row") {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => (r && r._uid ? r : { ...(r || {}), _uid: clientUid(prefix) }));
}
